import { Component, OnInit, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { JjamesButtonComponent } from 'shared-lib';
import {
    PaymentObligationService,
    PaymentObligationStatsDto,
    PaymentRevenueStatsDto,
    PaymentRevenueTrendDto,
    PaymentService,
    RevenueTrendInterval,
    RevenueTrendDto,
    RevenueTrendPointDto,
    TrendInterval
} from '../../../api/gen';

export type TimeRangePreset = '7d' | '30d' | '90d' | 'ytd' | 'all';

export interface ChartBarData {
    label: string;
    date: string;
    collected: number;
    unpaid: number;
    waived: number;
    volume: number;
    collectedHeightPct: number;
    unpaidHeightPct: number;
    waivedHeightPct: number;
    barHeightPct: number;
    maxVal: number;
}

export interface RailShareData {
    rail: string;
    labelKey: string;
    amount: number;
    count: number;
    sharePct: number;
    colorClass: string;
}

@Component({
    selector: 'app-reports-analytics',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        JjamesButtonComponent,
        TranslatePipe
    ],
    templateUrl: './reports-analytics.component.html',
    styleUrl: './reports-analytics.component.scss'
})
export class ReportsAnalyticsComponent implements OnInit {
    /** Site narrowing from parent */
    readonly parkingSpaceId = input<string>('');

    private obligationService = inject(PaymentObligationService);
    private paymentService = inject(PaymentService);
    private snackBar = inject(MatSnackBar);
    private translate = inject(TranslateService);

    readonly isLoading = signal(false);
    readonly isExporting = signal(false);

    /** Filter controls */
    readonly selectedPreset = signal<TimeRangePreset>('30d');
    readonly selectedInterval = signal<TrendInterval>(TrendInterval.Daily);

    /** Trend and statistics datasets */
    readonly trendData = signal<RevenueTrendDto | undefined>(undefined);
    readonly obligationStats = signal<PaymentObligationStatsDto | undefined>(undefined);
    readonly revenueStats = signal<PaymentRevenueStatsDto | undefined>(undefined);

    /**
     * Money actually taken, bucketed. Separate from trendData(), which buckets OBLIGATIONS: an
     * obligation is only raised when a stay leaves without paying, so its "collected" series is the
     * small slice of revenue that first went wrong and was later chased. Everything settled at the
     * column or on the parker's phone never becomes one and was missing from this chart entirely.
     */
    readonly revenueTrend = signal<PaymentRevenueTrendDto | undefined>(undefined);

    /**
     * Net revenue per bucket, keyed by the bucket's start instant. The two trends are requested over
     * the same window and interval and the backend truncates buckets identically (monthly to the
     * 1st, weekly to Monday), so the keys line up.
     */
    private readonly revenueByBucket = computed(() => {
        const map = new Map<number, number>();
        for (const p of this.revenueTrend()?.dataPoints ?? []) {
            if (p.date) map.set(new Date(p.date).getTime(), p.netRevenue ?? 0);
        }
        return map;
    });

    /** Hovered tooltip bar state */
    readonly activeBar = signal<ChartBarData | null>(null);

    readonly presets: { key: TimeRangePreset; labelKey: string }[] = [
        { key: '7d', labelKey: 'reports.period7d' },
        { key: '30d', labelKey: 'reports.period30d' },
        { key: '90d', labelKey: 'reports.period90d' },
        { key: 'ytd', labelKey: 'reports.periodYtd' },
        { key: 'all', labelKey: 'reports.periodAll' }
    ];

    readonly intervals: { key: TrendInterval; labelKey: string }[] = [
        { key: TrendInterval.Daily, labelKey: 'reports.intervalDaily' },
        { key: TrendInterval.Weekly, labelKey: 'reports.intervalWeekly' },
        { key: TrendInterval.Monthly, labelKey: 'reports.intervalMonthly' }
    ];

    /** Totals and Aggregates */
    /**
     * Money taken in the period, summed from the payment trend rather than the obligation trend.
     * The old sum counted only obligations that were later paid, so a site where everyone pays at
     * the column reported close to nothing collected.
     */
    readonly totalCollectedInPeriod = computed(() => {
        const points = this.revenueTrend()?.dataPoints ?? [];
        return points.reduce((sum, p) => sum + (p.netRevenue ?? 0), 0);
    });

    readonly totalUnpaidInPeriod = computed(() => {
        const points = this.trendData()?.dataPoints ?? [];
        return points.reduce((sum, p) => sum + (p.unpaidAmount ?? 0), 0);
    });

    readonly totalWaivedInPeriod = computed(() => {
        const points = this.trendData()?.dataPoints ?? [];
        return points.reduce((sum, p) => sum + (p.waivedAmount ?? 0), 0);
    });

    readonly totalTransactionsInPeriod = computed(() => {
        const points = this.trendData()?.dataPoints ?? [];
        return points.reduce((sum, p) => sum + (p.totalVolume ?? 0), 0);
    });

    readonly collectionRate = computed(() => {
        const collected = this.totalCollectedInPeriod();
        const unpaid = this.totalUnpaidInPeriod();
        const total = collected + unpaid;
        if (total === 0) return '—';
        return `${((collected / total) * 100).toFixed(1)}%`;
    });

    readonly currencyCode = computed(() => {
        return this.trendData()?.currency || this.revenueStats()?.currency || 'EUR';
    });

    readonly chartMinWidth = computed(() => {
        const count = this.chartBars().length;
        if (count === 0) return 0;
        return Math.max(500, count * 30);
    });

    /** Chart data points normalized for SVG rendering */
    readonly chartBars = computed<ChartBarData[]>(() => {
        const allPoints = this.trendData()?.dataPoints ?? [];
        if (allPoints.length === 0) return [];

        // Scaled against the revenue figure the bars actually draw, not the obligation one they used
        // to. Leaving collectedAmount here would size every bar against a maximum that is no longer
        // on the chart, flattening the whole series once real revenue exceeds chased receivables.
        const revenue = this.revenueByBucket();

        // Start the chart at the first bucket that has anything in it.
        //
        // Every preset counts backwards from today, so a site whose first payment is recent opens on
        // a run of empty buckets: with revenue beginning 2026-08-26 the default 30-day view led with
        // about a fortnight of nothing, which reads as a reporting outage rather than as "we had not
        // taken a payment yet". It also squashes the real bars, since the axis is shared.
        //
        // LEADING buckets only. A zero inside the period is a genuine day that took no money and has
        // to stay on the chart, and the tail is left alone so "nothing yet today" still shows.
        const hasActivity = (point: typeof allPoints[number]): boolean =>
            (revenue.get(new Date(point.date ?? 0).getTime()) ?? 0) > 0
            || (point.unpaidAmount ?? 0) > 0
            || (point.waivedAmount ?? 0) > 0
            || (point.totalVolume ?? 0) > 0;

        const firstWithData = allPoints.findIndex(hasActivity);
        if (firstWithData < 0) return [];

        const points = allPoints.slice(firstWithData);

        let highestAmount = 1;
        for (const p of points) {
            const collected = revenue.get(new Date(p.date ?? 0).getTime()) ?? 0;
            const total = collected + (p.unpaidAmount ?? 0) + (p.waivedAmount ?? 0);
            if (total > highestAmount) highestAmount = total;
        }

        const isMonthly = this.selectedInterval() === TrendInterval.Monthly;

        return points.map(p => {
            // Revenue, not receivables. p.collectedAmount counts only obligations that were paid.
            const collected = revenue.get(new Date(p.date ?? 0).getTime()) ?? 0;
            const unpaid = p.unpaidAmount ?? 0;
            const waived = p.waivedAmount ?? 0;
            const total = collected + unpaid + waived;

            const dateObj = p.date ? new Date(p.date) : null;
            let dateStr = '—';
            if (dateObj) {
                dateStr = isMonthly
                    ? dateObj.toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', year: '2-digit' })
                    : dateObj.toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' });
            }

            const totalHeightPct = (total / highestAmount) * 100;
            const barHeightPct = total > 0 ? Math.max(3, totalHeightPct) : 2;

            return {
                label: dateStr,
                date: p.date || '',
                collected,
                unpaid,
                waived,
                volume: p.totalVolume ?? 0,
                collectedHeightPct: (collected / highestAmount) * 100,
                unpaidHeightPct: (unpaid / highestAmount) * 100,
                waivedHeightPct: (waived / highestAmount) * 100,
                barHeightPct,
                maxVal: highestAmount
            };
        });
    });

    /** Payment Channels / Rails breakdown */
    readonly railShares = computed<RailShareData[]>(() => {
        const stats = this.revenueStats();
        const byKind = stats?.byProviderKind ?? {};
        const countByKind = stats?.countByProviderKind ?? {};

        const rails = [
            { rail: 'CardTerminal', labelKey: 'payments.rail_CardTerminal', colorClass: 'bg-purple-500' },
            { rail: 'Stripe', labelKey: 'payments.rail_Stripe', colorClass: 'bg-blue-500' },
            { rail: 'Im30', labelKey: 'payments.rail_Im30', colorClass: 'bg-teal-500' }
        ];

        const totalRevenue = stats?.netRevenue ?? 0;

        return rails.map(r => {
            const amount = byKind[r.rail] ?? 0;
            const count = countByKind[r.rail] ?? 0;
            const sharePct = totalRevenue > 0 ? (amount / totalRevenue) * 100 : 0;
            return {
                rail: r.rail,
                labelKey: r.labelKey,
                amount,
                count,
                sharePct: Math.max(0, Math.min(100, sharePct)),
                colorClass: r.colorClass
            };
        });
    });

    private readonly reloadOnSiteOrPresetChange = effect(() => {
        this.parkingSpaceId();
        this.selectedPreset();
        this.selectedInterval();

        untracked(() => {
            this.load();
        });
    });

    ngOnInit(): void {
        this.load();
    }

    setPreset(preset: TimeRangePreset): void {
        this.selectedPreset.set(preset);
        if (preset === '7d') this.selectedInterval.set(TrendInterval.Daily);
        else if (preset === '30d') this.selectedInterval.set(TrendInterval.Daily);
        else if (preset === '90d') this.selectedInterval.set(TrendInterval.Weekly);
        else if (preset === 'ytd') this.selectedInterval.set(TrendInterval.Weekly);
        else if (preset === 'all') this.selectedInterval.set(TrendInterval.Monthly);
    }

    setInterval(interval: TrendInterval): void {
        this.selectedInterval.set(interval);
    }

    /**
     * The obligation and payment contexts each declare their own interval enum. They serialize to the
     * same strings, but mapping beats casting: a cast keeps compiling if one side later gains a bucket
     * size the other does not have.
     */
    private revenueInterval(): RevenueTrendInterval {
        switch (this.selectedInterval()) {
            case TrendInterval.Monthly: return RevenueTrendInterval.Monthly;
            case TrendInterval.Weekly: return RevenueTrendInterval.Weekly;
            default: return RevenueTrendInterval.Daily;
        }
    }

    load(): void {
        this.isLoading.set(true);
        const { from, to } = this.calculateDateRange(this.selectedPreset());
        const siteId = this.parkingSpaceId() || undefined;

        forkJoin({
            trends: this.obligationService.paymentObligationsRevenueTrendsGet(
                from.toISOString(),
                to.toISOString(),
                this.selectedInterval(),
                siteId
            ),
            obStats: this.obligationService.paymentObligationsStatsGet(
                siteId, undefined, from.toISOString(), to.toISOString()
            ),
            revStats: this.paymentService.paymentsStatsGet(
                from.toISOString(), to.toISOString(), undefined, siteId
            ),
            revTrend: this.paymentService.paymentsRevenueTrendGet(
                from.toISOString(),
                to.toISOString(),
                this.revenueInterval(),
                undefined,
                siteId
            )
        }).subscribe({
            next: ({ trends, obStats, revStats, revTrend }) => {
                this.trendData.set(trends);
                this.obligationStats.set(obStats);
                this.revenueStats.set(revStats);
                this.revenueTrend.set(revTrend);
                this.isLoading.set(false);
            },
            error: (err) => {
                console.error('Failed to load reports analytics', err);
                this.isLoading.set(false);
                this.snackBar.open('Could not load reports analytics.', 'Dismiss', { duration: 5000 });
            }
        });
    }

    exportSummaryCsv(): void {
        if (this.isExporting()) return;
        const bars = this.chartBars();
        if (bars.length === 0) {
            this.snackBar.open('No data to export.', 'Dismiss', { duration: 4000 });
            return;
        }

        this.isExporting.set(true);
        try {
            const headers = ['Date', 'Collected Revenue (EUR)', 'Unpaid Bills (EUR)', 'Waived / Cancelled (EUR)', 'Transactions Count'];
            const quote = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;

            const lines = bars.map(b => [
                quote(b.date),
                b.collected.toFixed(2),
                b.unpaid.toFixed(2),
                b.waived.toFixed(2),
                b.volume
            ].join(','));

            const csv = [headers.join(','), ...lines].join('\r\n');
            const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `financial-report-summary-${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            this.snackBar.open('Report summary CSV downloaded.', 'Close', { duration: 4000 });
        } catch (err) {
            console.error('Failed to export report CSV', err);
            this.snackBar.open('Failed to export CSV.', 'Dismiss', { duration: 5000 });
        } finally {
            this.isExporting.set(false);
        }
    }

    private calculateDateRange(preset: TimeRangePreset): { from: Date; to: Date } {
        const to = new Date();
        const from = new Date();

        switch (preset) {
            case '7d':
                from.setDate(to.getDate() - 7);
                break;
            case '30d':
                from.setDate(to.getDate() - 30);
                break;
            case '90d':
                from.setDate(to.getDate() - 90);
                break;
            case 'ytd':
                from.setMonth(0, 1);
                from.setHours(0, 0, 0, 0);
                break;
            case 'all':
                from.setDate(to.getDate() - 365);
                break;
        }

        return { from, to };
    }
}
